import { ref, Ref } from 'vue';
import { getLoginService } from '@/container/ServiceRegistration';
import LoginResponse from '@/types/response/login';
import { ActionConstants } from '@/services/LoginService';
import crypto from 'crypto';

export interface LoginServiceComposable {
  isLoading: Ref<boolean>;
  error: Ref<string>;
  passwordError: Ref<string>;
  showPasswordValidationError: Ref<boolean>;
  login: (email: string, password: string, clientName: string) => Promise<LoginResponse | null>;
  register: (email: string, password: string, clientName: string) => Promise<LoginResponse | null>;
  updatePassword: (email: string, oldPassword: string, newPassword: string, clientName: string, nonce: string) => Promise<LoginResponse | null>;
  requestPasswordReset: (email: string, clientName: string) => Promise<LoginResponse | null>;
  portalResetPassword: (email: string, newPassword: string, clientName: string, nonce: string) => Promise<LoginResponse | null>;
  resetPassword: (email: string, newPassword: string, clientName: string, nonce: string) => Promise<LoginResponse | null>;
  validateSelfRegistration: (fields: Record<string, string>, clientName: string) => Promise<LoginResponse | null>;
  selfRegister: (firstName: string, lastName: string, email: string, password: string, nonce: string, clientName: string) => Promise<LoginResponse | null>;
  requestSelfRegistrationEmail: (firstName: string, lastName: string, email: string, nonce: string, clientName: string) => Promise<LoginResponse | null>;
  completeSelfRegistration: (email: string, newPassword: string, nonce: string, clientName: string) => Promise<LoginResponse | null>;
  encryptPassword: (password: string) => string;
}

export function useLoginService(): LoginServiceComposable {
  const isLoading = ref(false);
  const error = ref('');
  const passwordError = ref('');
  const showPasswordValidationError = ref(false);

  const encryptPassword = (password: string): string => {
    return crypto.createHash('sha512').update(password).digest('hex');
  };

  const login = async (email: string, password: string, clientName: string): Promise<LoginResponse | null> => {
    isLoading.value = true;
    error.value = '';

    try {
      const service = getLoginService();
      const response: LoginResponse = await service.login({
        Email: email,
        Password: password,
        EncryptedPassword: encryptPassword(password),
        Client: clientName,
        Action: ActionConstants.LOGIN
      });

      return response;
    } catch (err: any) {
      error.value = err.message || 'Server error. Please try again later.';
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const register = async (email: string, password: string, clientName: string): Promise<LoginResponse | null> => {
    isLoading.value = true;
    error.value = '';

    try {
      const service = getLoginService();
      const response: LoginResponse = await service.register({
        Email: email,
        EncryptedPassword: encryptPassword(password),
        Client: clientName,
        Action: ActionConstants.REGISTER
      });

      return response;
    } catch (err: any) {
      error.value = err.message || 'Server error. Please try again later.';
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const updatePassword = async (
    email: string, 
    oldPassword: string, 
    newPassword: string, 
    clientName: string, 
    nonce: string
  ): Promise<LoginResponse | null> => {
    isLoading.value = true;
    passwordError.value = '';

    try {
      const service = getLoginService();
      const response: LoginResponse = await service.updatePassword({
        Email: email,
        OldPassword: oldPassword,
        NewPassword: newPassword,
        Client: clientName,
        Nonce: nonce,
        Action: ActionConstants.UPDATE_PASSWORD
      });

      return response;
    } catch (err: any) {
      passwordError.value = err.message || 'Server error. Please try again later.';
      showPasswordValidationError.value = true;
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const requestPasswordReset = async (
    email: string,
    clientName: string
  ): Promise<LoginResponse | null> => {
    isLoading.value = true;
    error.value = '';

    try {
      const service = getLoginService();
      // Using requestPasswordReset endpoint with REQUEST_PASSWORD_RESET action
      // Backend will send password reset email with nonce
      const response: LoginResponse = await service.requestPasswordReset({
        Email: email,
        Client: clientName,
        Action: ActionConstants.REQUEST_PASSWORD_RESET
      });

      return response;
    } catch (err: any) {
      error.value = err.message || 'Server error. Please try again later.';
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const resetPassword = async (
    email: string,
    newPassword: string,
    clientName: string,
    nonce: string
  ): Promise<LoginResponse | null> => {
    isLoading.value = true;
    error.value = '';

    try {
      const service = getLoginService();
      // Using updatePassword endpoint with RESET_PASSWORD action
      // Backend validates nonce and updates password
      const response: LoginResponse = await service.updatePassword({
        Email: email,
        NewPassword: newPassword,
        Client: clientName,
        Nonce: nonce,
        Action: ActionConstants.RESET_PASSWORD
      });

      return response;
    } catch (err: any) {
      error.value = err.message || 'Server error. Please try again later.';
      return null;
    } finally {
      isLoading.value = false;
    }
  };
  const portalResetPassword = async (
    email: string,
    newPassword: string,
    clientName: string,
    nonce: string
  ): Promise<LoginResponse | null> => {
    isLoading.value = true;
    error.value = '';

    try {
      const service = getLoginService();
      // Using updatePassword endpoint with RESET_PASSWORD action
      // Backend validates nonce and updates password
      const response: LoginResponse = await service.resetPassword({
        Email: email,
        NewPassword: encryptPassword(newPassword),
        Client: clientName,
        Nonce: nonce,
        Action: ActionConstants.PORTAL_RESET_PASSWORD
      });

      return response;
    } catch (err: any) {
      error.value = err.message || 'Server error. Please try again later.';
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const validateSelfRegistration = async (
    fields: Record<string, string>,
    clientName: string
  ): Promise<LoginResponse | null> => {
    isLoading.value = true;
    error.value = '';

    try {
      const service = getLoginService();
      const response: LoginResponse = await service.validateSelfRegistration({
        Fields: fields,
        Client: clientName,
        Action: ActionConstants.VALIDATE_SELF_REGISTRATION
      });

      return response;
    } catch (err: any) {
      error.value = err.message || 'Server error. Please try again later.';
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const selfRegister = async (
    firstName: string,
    lastName: string,
    email: string,
    password: string,
    nonce: string,
    clientName: string
  ): Promise<LoginResponse | null> => {
    isLoading.value = true;
    error.value = '';

    try {
      const service = getLoginService();
      const response: LoginResponse = await service.selfRegister({
        FirstName: firstName,
        LastName: lastName,
        Email: email,
        EncryptedPassword: encryptPassword(password),
        Nonce: nonce,
        Client: clientName,
        Action: ActionConstants.SELF_REGISTER
      });

      return response;
    } catch (err: any) {
      error.value = err.message || 'Server error. Please try again later.';
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const requestSelfRegistrationEmail = async (
    firstName: string,
    lastName: string,
    email: string,
    nonce: string,
    clientName: string
  ): Promise<LoginResponse | null> => {
    isLoading.value = true;
    error.value = '';

    try {
      const service = getLoginService();
      const response: LoginResponse = await service.selfRegister({
        FirstName: firstName,
        LastName: lastName,
        Email: email,
        Nonce: nonce,
        Client: clientName,
        Action: ActionConstants.REQUEST_SELF_REGISTRATION_EMAIL
      });

      return response;
    } catch (err: any) {
      error.value = err.message || 'Server error. Please try again later.';
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const completeSelfRegistration = async (
    email: string,
    newPassword: string,
    nonce: string,
    clientName: string
  ): Promise<LoginResponse | null> => {
    isLoading.value = true;
    error.value = '';

    try {
      const service = getLoginService();
      const response: LoginResponse = await service.selfRegister({
        Email: email,
        EncryptedPassword: encryptPassword(newPassword),
        Nonce: nonce,
        Client: clientName,
        Action: ActionConstants.COMPLETE_SELF_REGISTRATION
      });

      return response;
    } catch (err: any) {
      error.value = err.message || 'Server error. Please try again later.';
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    error,
    passwordError,
    showPasswordValidationError,
    login,
    register,
    updatePassword,
    requestPasswordReset,
    resetPassword,
    portalResetPassword,
    validateSelfRegistration,
    selfRegister,
    requestSelfRegistrationEmail,
    completeSelfRegistration,
    encryptPassword
  };
}
